import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { FullUserFragment, UserPageUsersGQL } from 'graphql.generated';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { BehaviorSubject, filter, map, switchMap, tap } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-select-user-ids',
  templateUrl: './select-user-ids.component.html',
  styleUrl: './select-user-ids.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectUserIdsComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: SelectUserIdsComponent,
    },
  ],
})
export class SelectUserIdsComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {

  @Input() userRecord: Record<string, FullUserFragment> = {};

  @Output() userRecordChange = new EventEmitter<typeof this.userRecord>();

  userIds: string[];

  selectedUsers: FullUserFragment[] = [];

  suggestions: FullUserFragment[] = [];

  touched = false;

  disabled = false;

  _searchInput$ = new BehaviorSubject('');

  searchInput$ = this._searchInput$.asObservable();

  subs = new SubSink();

  constructor(
    private userPageUsersGQL: UserPageUsersGQL,
  ) {}

  ngOnInit(): void {
    this.subs.sink = this.searchInput$.pipe(
      tap((input) => {
        if (!input) {
          this.suggestions = [];
        }
      }),
      filter((input) => Boolean(input)),
      switchMap((search) => this.userPageUsersGQL.fetch({ filter: { search, showDeactivated: true } })),
      filter((res) => (res?.data?.usersv2?.users?.length || 0) > 0),
      map((res) => res.data.usersv2.users),
    ).subscribe((users) =>this.suggestions = users);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onChange = (_userIds: string[]) => {};

  onTouched = () => {}

  writeValue(userIds: string[]): void {
    this.userIds = userIds;

    this.selectedUsers = userIds
      .map((id) => this.userRecord[id])
      .filter(Boolean);
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  validate(control: AbstractControl<any, any>): ValidationErrors {

    const userIds = control.value;

    if (!Array.isArray(userIds)) {
      return {
        mustBeArray: true,
      }
    }

    return null;
  }

  searchUser(event: AutoCompleteCompleteEvent) {
    this._searchInput$.next(event.query);
  }

  handleOnSelect() {
    this.setUserIds();
    this.updateUserRecord();
  }

  setUserIds() {
    this.userIds = this.selectedUsers.map((u) => u.id);
    this.onChange(this.userIds);
  }

  updateUserRecord() {

    for (const user of this.selectedUsers) {
      this.userRecord[user.id] = user;
    }

    this.userRecordChange.emit(this.userRecord);
  }
}
